import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader, CardTitle, Spinner, Alert, Form, FormGroup, Label, Input, Button, CardFooter } from "reactstrap";
import { useStaffStore } from "../../stores/Staffs";
import { tokenHelper } from "../../stores/helpers";
import { toast } from "react-toastify";

const RegisterFromInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invitationData, setInvitationData] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [wasLoggedIn, setWasLoggedIn] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    acceptTerms: false
  });
  
  // Get store methods and state
  const { 
    verifyInvitationToken, 
    registerWithInvitation,
    verifyInvitationTokenAccepted,
    isLoading, 
    isError, 
    error 
  } = useStaffStore();

  useEffect(() => {
    // Check if user is already logged in and log them out
    // const existingToken = tokenHelper.getToken();
    // if (existingToken) {
    //   console.log("User already logged in, logging out to register new user");
    //   setWasLoggedIn(true);
    //   tokenHelper.removeToken();
    // }
    
    verifyInvitation();
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const data = await verifyInvitationTokenAccepted(token);
      setInvitationData(data);
      
      // Pre-fill email from invitation
      setFormData(prev => ({
        ...prev,
        email: data.email
      }));
    } catch (err) {
      console.error("Error verifying invitation:", err);
      // Error is handled by the store
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      alert("Name is required");
      return false;
    }
    if (!formData.email.trim()) {
      alert("Email is required");
      return false;
    }
    // Ensure the email matches the invitation
    if (invitationData && formData.email !== invitationData.email) {
      alert(`Email must match the invitation email: ${invitationData.email}`);
      return false;
    }
    if (!formData.password) {
      alert("Password is required");
      return false;
    }
    if (formData.password.length < 6) {
      alert("Password must be at least 6 characters long");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match");
      return false;
    }
    if (!formData.acceptTerms) {
      alert("Please accept the terms and conditions");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setRegistering(true);

      // Register the user and accept invitation
      const registerData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null
      };
      // Register the user with invitation (this handles everything in one call)
      const response = await registerWithInvitation(registerData, token);
      
      // Store the token
      tokenHelper.setToken(response.token);

      // Show success message and redirect
      toast.success("Registration successful!");
      navigate("/auth/registration-success");
      
    } catch (err) {
      console.error("Error during registration:", err);
      // Error is handled by the store
    } finally {
      setRegistering(false);
    }
  };

  if (isLoading) {
    return (
      <div className="auth-page">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <Card>
                <CardBody className="text-center p-5">
                  <Spinner color="primary" className="mb-3" />
                  <h5>Verifying invitation...</h5>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError && !invitationData) {
    return (
      <div

      className="auth-page">
        <div  className="container">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <Card>
                <CardHeader>
                  <CardTitle className="text-center text-danger">
                    Invalid Invitation
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <Alert color="danger">
                    {error}
                  </Alert>
                  <div className="text-center mt-3">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/login")}
                    >
                      Go to Login
                    </button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
          <div className="d-flex justify-content-center align-items-center flex-column" style={{ backgroundColor:"rgba(206, 149, 88, 0.7)" ,height: "100vh" }}>
            <Card style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 0, 0, 0.3)'
            }}>
              <CardHeader className="text-center">
                <CardTitle className="h4 mb-0">
                  <i className="ri-user-add-line me-2"></i>
                  Complete Your Registration
                </CardTitle>
                <p className="text-muted mt-2 mb-0">
                  You've been invited to join <strong>{invitationData?.site?.name}</strong>
                </p>
              </CardHeader>
              <CardBody>
                {wasLoggedIn && (
                  <Alert color="warning" className="mb-3">
                    <i className="ri-information-line me-2"></i>
                    <strong>Account Switch Required:</strong> You were logged in with a different account. 
                    We've logged you out so you can register with the invited email address.
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <FormGroup className="mb-3">
                        <Label for="name">Full Name *</Label>
                        <Input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          required
                        />
                      </FormGroup>
                    </div>
                    <div className="col-md-6">
                      <FormGroup className="mb-3">
                        <Label for="email">Email Address *</Label>
                        <Input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your email"
                          required
                          readOnly
                          className="bg-light"
                        />
                        <small className="text-muted">This email was used for the invitation and cannot be changed</small>
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <FormGroup className="mb-3">
                        <Label for="password">Password *</Label>
                        <Input
                          type="password"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          placeholder="Enter your password"
                          required
                          minLength={6}
                        />
                      </FormGroup>
                    </div>
                    <div className="col-md-6">
                      <FormGroup className="mb-3">
                        <Label for="confirmPassword">Confirm Password *</Label>
                        <Input
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirm your password"
                          required
                        />
                      </FormGroup>
                    </div>
                  </div>

                  <FormGroup className="mb-3">
                    <Label for="phone">Phone Number (Optional)</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                    />
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <div className="form-check">
                      <Input
                        type="checkbox"
                        id="acceptTerms"
                        name="acceptTerms"
                        checked={formData.acceptTerms}
                        onChange={handleInputChange}
                        required
                      />
                      <Label for="acceptTerms" className="form-check-label">
                        I agree to the <a href="#" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="#" target="_blank" rel="noopener noreferrer">Privacy Policy</a> *
                      </Label>
                    </div>
                  </FormGroup>

                  {isError && (
                    <Alert color="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}

                  <div className="text-center">
                    <Button
                      type="submit"
                      style={{ backgroundColor: "#ce9558", color: "#fff" }}
                      size="md"
                      disabled={registering}
                      className="w-100"
                    >
                      {registering ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Creating Account...
                        </>
                      ) : (
                        <>
                          <i className="ri-user-add-line me-2"></i>
                          Complete Registration & Accept Invitation
                        </>
                      )}
                    </Button>
                  </div>
                </Form>

      
              </CardBody>
            </Card>
            <div className="d-flex justify-content-center align-items-center flex-column">
                <img src="/logo.png" alt="logo" style={{ width: "30px" }} />
                <p className="text-white">
                  &copy; {new Date().getFullYear()} ClickOrder.
                </p>
               </div>
          </div>

  );
};

export default RegisterFromInvitation;
